import React, { useState, useEffect } from "react";
import {
  Users,
  CreditCard,
  UserCheck,
  DollarSign,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import jsPDF from "jspdf";
import api from "../../../services/api";
import { capitalize } from "../../../services/helper";



function Pagination({ pagination, onPageChange }) {
  const { page, totalPages, hasNext, hasPrev } = pagination || {};

  if (!totalPages || totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-3 mt-10">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={!hasPrev}
        className="flex items-center gap-1 px-4 py-2 rounded-xl border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <ChevronLeft size={18} />
        Previous
      </button>

      <span className="text-sm text-gray-600 font-medium">
        Page {page} of {totalPages}
      </span>

      <button
        onClick={() => onPageChange(page + 1)}
        disabled={!hasNext}
        className="flex items-center gap-1 px-4 py-2 rounded-xl border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        Next
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

export default function MySubscriptions() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // TanStack Query
  const { data, isLoading } = useQuery({
    queryKey: ["paymentLists", page],
    queryFn: () => api.getPayments(page),
    keepPreviousData: true,
  });

  const payments = data?.data || [];
  const statsData = data?.stats || {};
  const pagination = data?.pagination || { page: 1, totalPages: 1, hasNext: false, hasPrev: false };

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [search]);

  const stats = [
    {
      label: "Total Users",
      value: statsData.totalUsers?.toLocaleString() || "0",
      icon: Users,
      highlight: false,
    },
    {
      label: "Pilgrim Users",
      value: statsData.pilgrimUsers?.toLocaleString() || "0",
      icon: CreditCard,
      highlight: false,
    },
    {
      label: "Free Users",
      value: statsData.freeUsers?.toLocaleString() || "0",
      icon: UserCheck,
      highlight: false,
    },
    {
      label: "Revenue",
      value: `$${parseFloat(statsData.totalRevenue || 0).toLocaleString()}`,
      icon: DollarSign,
      highlight: true,
    },
  ];

  const downloadInvoice = (payment) => {
    const doc = new jsPDF();
    let yPos = 20;

    // Header
    doc.setFontSize(24);
    doc.setTextColor(31, 58, 96);
    doc.setFont(undefined, "bold");
    doc.text("INVOICE", 14, yPos);
    yPos += 15;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Subscription Management System", 14, yPos);
    // yPos += 6;
    // doc.text("123 Business Street, New York, NY 10001", 14, yPos);
    yPos += 6;
    doc.text("info@fa1thhook.com", 14, yPos);
    yPos += 10;

    // Invoice Info
    doc.setFillColor(248, 248, 248);
    doc.rect(14, yPos, 180, 40, "F");
    doc.rect(14, yPos, 180, 40);

    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont(undefined, "bold");

    doc.text("Invoice Number:", 20, yPos + 10);
    doc.text("Date:", 20, yPos + 20);
    doc.text("Status:", 20, yPos + 30);

    doc.setFont(undefined, "normal");
    doc.text(`PAY-${payment.id}`, 75, yPos + 10);
    doc.text(new Date(payment.created_at).toLocaleDateString(), 75, yPos + 20);
    doc.text(payment.status.toUpperCase(), 75, yPos + 30);

    yPos += 55;

    // Bill To
    doc.setFontSize(12);
    doc.setTextColor(31, 58, 96);
    doc.setFont(undefined, "bold");
    doc.text("Bill To:", 14, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");
    doc.text(payment.user_name || "Customer", 14, yPos);
    yPos += 6;
    doc.text(payment.user_email || "", 14, yPos);
    yPos += 25;

    // Table Header
    const col1 = 14;
    const col2 = 90;
    const col3 = 140;
    const col4 = 170;

    doc.setFillColor(31, 58, 96);
    doc.rect(col1, yPos, 76, 12, "F");
    doc.rect(col2, yPos, 50, 12, "F");
    doc.rect(col3, yPos, 30, 12, "F");
    doc.rect(col4, yPos, 30, 12, "F");

    doc.setTextColor(255);
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");

    doc.text("Description", col1 + 3, yPos + 8);
    doc.text("Plan", col2 + 3, yPos + 8);
    doc.text("Amount", col3 + 3, yPos + 8);
    doc.text("Total", col4 + 3, yPos + 8);

    yPos += 12;

    // Table Row
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");

    doc.rect(col1, yPos, 76, 12);
    doc.rect(col2, yPos, 50, 12);
    doc.rect(col3, yPos, 30, 12);
    doc.rect(col4, yPos, 30, 12);

    doc.text(payment.description || payment.plan_name || "Subscription", col1 + 3, yPos + 8);
    doc.text(payment.plan_name || "N/A", col2 + 3, yPos + 8);
    doc.text(`$${payment.amount}`, col3 + 3, yPos + 8);
    doc.text(`$${payment.amount}`, col4 + 3, yPos + 8);

    yPos += 30;

    // Total
    doc.setFillColor(248, 248, 248);
    doc.rect(130, yPos, 64, 28, "F");
    doc.rect(130, yPos, 64, 28);

    doc.text("Total:", 136, yPos + 10);
    doc.setFont(undefined, "bold");
    doc.setTextColor(31, 58, 96);
    doc.text(`$${payment.amount}`, 188, yPos + 10, { align: "right" });

    // Footer
    yPos += 45;
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.setFont(undefined, "normal");
    // doc.text("Thank you for your business!", 14, yPos);

    doc.save(`Invoice_${payment.id}_${(payment.user_name || "customer").replace(/\s/g, "_")}.pdf`);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }} className="bg-gray-50 p-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Subscriptions
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Manage plans, pricing, and payment history
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div
              key={i}
              className={`w-full min-h-[174px] rounded-3xl px-5 sm:px-6 py-6 sm:py-7 transition-all duration-300 ${
                stat.highlight
                  ? "bg-gradient-to-r from-[#1F3A60] to-[#4078C6] text-white"
                  : "bg-white border border-gray-100"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className={`text-[16px] leading-6 font-medium ${stat.highlight ? "text-blue-100" : "text-gray-500"}`}>
                  {stat.label}
                </p>
                <div
                  className={`w-12 h-12 sm:w-[51px] sm:h-[51px] rounded-2xl flex items-center justify-center flex-shrink-0 ${
                    stat.highlight ? "bg-white/10" : "bg-gray-100"
                  }`}
                >
                  <Icon size={28} className={stat.highlight ? "text-white" : "text-gray-400"} />
                </div>
              </div>

              <h3 className={`text-[45px] sm:text-4xl lg:text-[45px] leading-tight font-bold tracking-[-1px] ${stat.highlight ? "text-white" : "text-gray-900"}`}>
                {stat.value}
              </h3>
            </div>
          );
        })}
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-3xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 md:px-6 py-4 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-700">Recent Payments</span>

          {/* <div className="relative w-full sm:w-[420px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search here..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-blue-400 bg-gray-50 placeholder-gray-400"
            />
          </div> */}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">ID</th>
                <th className="text-left px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">User</th>
                <th className="text-left px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Plan</th>
                <th className="text-left px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                <th className="text-left px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-center px-4 md:px-6 py-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">Invoice</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400">
                    Loading payments...
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                    No payments found.
                  </td>
                </tr>
              ) : (
                payments.map((row) => (
                  <tr key={row.id} className="border-b border-gray-300 hover:bg-gray-50 transition-colors">
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-700 whitespace-nowrap">{row.id}</td>
                    <td className="px-4 md:px-6 py-4 text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {row.user_name}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-700 whitespace-nowrap">{row.plan_name}</td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      ${parseFloat(row.amount).toFixed(2)}
                    </td>
                    <td className="px-4 md:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(row.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          row.status === "succeeded"
                            ? "text-teal-600 bg-teal-50 border border-teal-200"
                            : "text-amber-600 bg-amber-50 border border-amber-200"
                        }`}
                      >
                        {capitalize(row.status)}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => downloadInvoice(row)}
                        className="w-9 h-9 mx-auto hover:bg-blue-50 flex items-center justify-center transition-colors group"
                      >
                        <Download size={20} strokeWidth={2.5} className="text-black group-hover:text-blue-600" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={setPage} />
    </div>
  );
}